import { useBrowseMatch } from "@/features/browse/hooks/navigation/useBrowseMatch";
import { useBrowseNavigation } from "@/features/browse/hooks/navigation/useBrowseNavigation";
import { HeaderSlotComponent } from "@/types/header.types";
import { Switch } from "@fido/ui-lib";
import { useState } from "react";
import { useTranslation } from "react-i18next";

/**
 * Example slot component for project-related actions
 * This can be expanded based on actual requirements
 */
export const ProjectActionsSlot: HeaderSlotComponent = ({ search }) => {
  const projectName = search.get("project-name");

  if (!projectName) return null;

  return (
    <div className="flex items-center">
      <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
        {projectName}
      </span>
    </div>
  );
};

/**
 * Example slot component for waypoint-specific UI
 */
export const WaypointActionsSlot: HeaderSlotComponent = ({
  params,
  search
}) => {
  const { t } = useTranslation();
  const waypointId = params.id || search.get("waypoint");

  if (!waypointId) return null;

  return (
    <div className="flex items-center">
      <span className="text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded">
        {t("BROWSE.TABLE.ID_PREFIX")} {waypointId}
      </span>
    </div>
  );
};

export const BrowseViewSwitchSlot: HeaderSlotComponent = () => {
  const { t } = useTranslation();
  const { isInteractive } = useBrowseMatch();
  const { switchViewMode } = useBrowseNavigation();
  const [isSwitching, setIsSwitching] = useState(false);

  const handleCheckedChange = async (checked: boolean) => {
    if (isSwitching || checked === !isInteractive) return;

    setIsSwitching(true);
    try {
      await switchViewMode();
    } finally {
      setIsSwitching(false);
    }
  };

  return (
    <div className="flex items-center gap-2">
      <Switch
        checked={!isInteractive}
        onCheckedChange={handleCheckedChange}
        disabled={isSwitching}
      />
      <span className="text-sm text-gray-600">
        {t("COMMON.VIEW.LIST_VIEW")}
      </span>
    </div>
  );
};

/**
 * Registry of available header slot components
 *
 * To add a new slot:
 * 1. Create your component following the HeaderSlotComponent type
 * 2. Add it to this registry with a unique key
 * 3. Use the key in your route's handle.header.extraSlots
 *
 * Example:
 * ```tsx
 * // In your route definition:
 * handle: {
 *   header: {
 *     titleKey: "PROJECT_STATUS",
 *     extraSlots: ["project-actions"]
 *   }
 * }
 * ```
 */
